import { CalendarBlank, Check, Link as LinkIcon, LinkSimple, Lock, Sparkle } from '@phosphor-icons/react';
import { type BriefAsset, type DeliverableDetails, type Version } from '@mybusinessmate/schema';
import { signedDownloadUrl } from '../../lib/asset-url';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';
import { useToast } from '../ui/toast';
import { AssetImage } from './asset-image';
import { CopyPanel, FactRow, Field, Maybe, platformsText, when } from './brief-preview';

/** A slide, a screenshot or a video still: the picture fills it, "1/4" sits top left and its name bottom left. */
export function SlideTile({ asset, index, total }: { asset: BriefAsset; index: number; total: number }) {
  return (
    <li className="relative flex aspect-square flex-col justify-between overflow-hidden rounded-(--mbm-radius-sm) bg-surface-field p-(--mbm-space-2)">
      <AssetImage asset={asset} className="absolute inset-0" />
      <span className="relative self-start rounded-(--mbm-radius-xs) bg-overlay-scrim px-(--mbm-space-1-5) text-label-small text-text-on-dark">
        {index + 1}/{total}
      </span>
      <span className="relative self-start truncate rounded-(--mbm-radius-xs) bg-overlay-scrim px-(--mbm-space-1-5) text-label-medium text-text-on-dark">
        {asset.label || asset.name}
      </span>
    </li>
  );
}

/** The picture row of a post or a reel's cover: four across (Figma A05.4 version pane, A05.6 draft card). */
export function SlideGrid({ assets }: { assets: BriefAsset[] }) {
  if (assets.length === 0) return null;
  return (
    <ul className="grid grid-cols-2 gap-(--mbm-space-2) sm:grid-cols-4">
      {assets.map((asset, index) => (
        <SlideTile key={asset.id} asset={asset} index={index} total={assets.length} />
      ))}
    </ul>
  );
}

/** The small blue tag beside a label ("Shortened vs brief", "Your notes on v1 addressed"). */
export function ChangedChip({ children }: { children: string }) {
  return (
    <span className="inline-flex rounded-(--mbm-radius-xs) bg-feedback-info-bg px-(--mbm-space-1-5) py-px text-label-tag text-feedback-info">
      {children}
    </span>
  );
}

/** A file you open rather than see (the site's source zip, a reel's video): through a signed link, never a fixed URL. */
export function FileLink({ asset }: { asset: BriefAsset }) {
  const toast = useToast();
  const open = async () => {
    try {
      window.open(await signedDownloadUrl(asset.key), '_blank', 'noopener,noreferrer');
    } catch {
      toast({ title: 'Couldn’t open the file', description: asset.name, tone: 'error' });
    }
  };
  return (
    <button
      type="button"
      onClick={() => void open()}
      className="inline-flex max-w-full items-center gap-(--mbm-space-2) rounded-full bg-surface-tint px-(--mbm-space-3) py-(--mbm-space-1) text-label-medium text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
    >
      <Icon icon={LinkSimple} size="sm" />
      <span className="truncate">{asset.name}</span>
    </button>
  );
}

const SSL_LABEL: Record<string, string> = {
  Active: 'Active',
  Pending: 'Pending',
  'Not Started': 'None',
  Error: 'Error',
};

function Swatch({ color }: { color: string }) {
  return (
    <span className="inline-flex items-center gap-(--mbm-space-2)">
      <span
        aria-hidden
        className="size-(--mbm-space-4) rounded-full border border-border-line"
        style={{ background: color }}
      />
      {color}
    </span>
  );
}

/** What a website version holds: where it lives, how to get in, the colours, and each section ticked or not. */
function WebsiteSections({ details }: { details: Extract<DeliverableDetails, { type: 'website' }> }) {
  const built = details.sections.filter((s) => s.built).length;
  return (
    <div className="flex flex-col gap-(--mbm-space-3)">
      <div className="flex items-baseline justify-between gap-(--mbm-space-3)">
        <span className="text-label-small text-text-tertiary uppercase">Sections</span>
        <span className="text-label-medium text-text-primary">
          {built} of {details.sections.length} built
        </span>
      </div>
      <ul className="flex flex-col gap-(--mbm-space-2)">
        {details.sections.map((section) => (
          <li
            key={section.title}
            className="flex items-center gap-(--mbm-space-3) rounded-(--mbm-radius-md) border border-border-line p-(--mbm-space-2)"
          >
            <span className="relative size-(--mbm-size-section-thumb) shrink-0 overflow-hidden rounded-(--mbm-radius-sm) bg-surface-field">
              {section.image ? <AssetImage asset={section.image} /> : null}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-label-large text-text-primary">
                {section.title}
                {section.kind ? (
                  <span className="ml-(--mbm-space-2) text-label-tag text-text-tertiary">{section.kind}</span>
                ) : null}
              </span>
              {section.description ? (
                <span className="truncate text-body-small text-text-secondary">{section.description}</span>
              ) : null}
            </span>
            <span
              aria-label={section.built ? 'Built' : 'Not built'}
              className={cx(
                'flex size-(--mbm-size-checkbox) shrink-0 items-center justify-center rounded-(--mbm-component-radius-checkbox) border-[1.5px]',
                section.built
                  ? 'border-action-primary bg-action-primary text-text-on-dark'
                  : 'border-border-strong text-transparent',
              )}
            >
              <Icon icon={Check} size="sm" />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A version's content as the version pane shows it (Figma A05.4): pictures, then the words the client will read on a
 * grey panel, then the practical facts. Staff only: the client's own review page has its own wording.
 */
export function DeliverablePreview({ version }: { version: Version }) {
  const { details, assets } = version;
  switch (details.type) {
    case 'post':
      return (
        <div className="flex flex-col gap-(--mbm-space-4)">
          <SlideGrid assets={assets} />
          <CopyPanel label="Caption" tags={details.hashtags}>
            {details.caption}
          </CopyPanel>
          <FactRow
            items={[
              {
                icon: CalendarBlank,
                text: details.scheduledUploadAt ? when.format(new Date(details.scheduledUploadAt)) : null,
              },
              { icon: Sparkle, text: details.audioNote },
              { icon: LinkIcon, text: platformsText(details.platforms) },
            ]}
          />
        </div>
      );
    case 'reel':
      return (
        <div className="flex flex-col gap-(--mbm-space-4)">
          <SlideGrid assets={assets} />
          {details.videoUrl ? (
            <Field label="Video link">
              <a
                href={details.videoUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="text-brand-primary break-all"
              >
                {details.videoUrl}
              </a>
            </Field>
          ) : null}
          <CopyPanel label="Caption" tags={details.hashtags}>
            {details.caption}
          </CopyPanel>
          <FactRow
            items={[
              {
                icon: CalendarBlank,
                text: details.scheduledUploadAt ? when.format(new Date(details.scheduledUploadAt)) : null,
              },
              { icon: Sparkle, text: details.audioNote },
              { icon: LinkIcon, text: platformsText(details.platforms) },
            ]}
          />
        </div>
      );
    case 'website':
      return (
        <div className="flex flex-col gap-(--mbm-space-4)">
          <dl className="grid gap-(--mbm-space-4) sm:grid-cols-2">
            <Maybe label="Preview / live URL">
              {details.liveUrl ? (
                <a
                  href={details.liveUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-brand-primary break-all"
                >
                  {details.liveUrl}
                </a>
              ) : null}
            </Maybe>
            <Maybe label="SSL">{SSL_LABEL[details.sslStatus]}</Maybe>
            <Maybe label="Admin panel">{details.adminPanelUrl}</Maybe>
            <Maybe label="Admin username">{details.adminUsername}</Maybe>
            <Maybe label="Colours">
              <span className="flex flex-wrap gap-x-(--mbm-space-4) gap-y-(--mbm-space-1)">
                {details.primaryColor ? <Swatch color={details.primaryColor} /> : null}
                {details.secondaryColor ? <Swatch color={details.secondaryColor} /> : null}
              </span>
            </Maybe>
            <Maybe label="Logo used">{details.logoNotes}</Maybe>
          </dl>
          {assets.length > 0 ? (
            <div className="flex flex-col gap-(--mbm-space-2)">
              <span className="text-label-small text-text-tertiary uppercase">Source files</span>
              <ul className="flex flex-wrap gap-(--mbm-space-2)">
                {assets.map((asset) => (
                  <li key={asset.id}>
                    <FileLink asset={asset} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {details.sections.length > 0 ? <WebsiteSections details={details} /> : null}
          <p className="flex items-center gap-(--mbm-space-2) text-body-small text-text-tertiary">
            <Icon icon={Lock} size="sm" />
            Logins stay in the encrypted vault, not here.
          </p>
        </div>
      );
    case 'monthly_update':
      return (
        <div className="flex flex-col gap-(--mbm-space-4)">
          <SlideGrid assets={assets} />
          <Maybe label="Page">{details.pageUrl}</Maybe>
          <CopyPanel label="What changed">{details.resolvedNotes}</CopyPanel>
        </div>
      );
    case 'custom':
      return (
        <div className="flex flex-col gap-(--mbm-space-4)">
          <SlideGrid assets={assets} />
        </div>
      );
  }
}
